import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import ViewTabs from '../components/ViewTabs';
import { useT } from '../context/LocaleContext';
import { supabase } from '../lib/supabase';
import { BOOKING_SELECT } from '../lib/bookingData';
import {
  buildResaleDesk,
  confirmedCardMeta,
  deskItemHref,
  isResaleBooking,
  pendingBadge,
  pendingCardMeta,
  pendingCardTitle,
} from '../lib/resaleDesk';

export default function ResaleInspectionsPage() {
  const t = useT();
  const [bookings, setBookings] = useState([]);
  const [reports, setReports] = useState([]);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [tab, setTab] = useState('pending');
  const [deletingId, setDeletingId] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  useEffect(() => {
    let alive = true;
    async function load() {
      const [bookingRes, reportRes] = await Promise.all([
        supabase
          .from('bookings')
          .select(BOOKING_SELECT)
          .order('scheduled_at', { ascending: true })
          .limit(200),
        supabase
          .from('resale_inspection_reports')
          .select('*')
          .order('created_at', { ascending: false })
          .limit(200),
      ]);
      if (!alive) return;
      if (bookingRes.error || reportRes.error) {
        setError(bookingRes.error?.message || reportRes.error?.message);
        return;
      }
      setBookings((bookingRes.data || []).filter(isResaleBooking));
      setReports(reportRes.data || []);
    }
    load();
    return () => {
      alive = false;
    };
  }, []);

  const desk = useMemo(() => buildResaleDesk(bookings, reports), [bookings, reports]);

  const items = tab === 'pending' ? desk.pending : desk.confirmed;

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((item) => {
      const haystack =
        item.kind === 'finalized'
          ? [
              item.report.report_number,
              item.report.seller_name,
              item.report.seller_phone,
              item.report.plate_number,
              item.report.vin,
              item.report.brand,
              item.report.model,
            ]
          : item.kind === 'booking'
            ? [
                item.booking.service_type,
                item.booking.profiles?.phone,
                item.booking.vehicles?.plate_number,
                item.booking.vehicles?.vin,
                item.report?.report_number,
              ]
            : [
                item.report.report_number,
                item.report.seller_name,
                item.report.plate_number,
              ];
      return haystack
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(q);
    });
  }, [items, query]);

  async function deleteDraft(reportId, event) {
    event.preventDefault();
    event.stopPropagation();
    if (confirmDeleteId !== reportId) {
      setConfirmDeleteId(reportId);
      return;
    }
    setDeletingId(reportId);
    setError('');
    try {
      const { error: deleteError } = await supabase
        .from('resale_inspection_reports')
        .delete()
        .eq('id', reportId)
        .eq('status', 'draft');
      if (deleteError) throw new Error(deleteError.message);
      setReports((current) => current.filter((row) => row.id !== reportId));
      setConfirmDeleteId(null);
    } catch (err) {
      setError(err.message || 'Could not delete draft.');
    } finally {
      setDeletingId(null);
    }
  }

  return (
    <section className="resale-page">
      <header className="page-head">
        <div>
          <span className="kicker">{t('resale.kicker')}</span>
          <h1>{t('resale.title')}</h1>
          <p className="lede">
            {t('resale.lede')}
          </p>
        </div>
        <Link to="/resale-inspections/new" className="primary">
          {t('resale.walkIn')}
        </Link>
      </header>

      <ViewTabs
        value={tab}
        onChange={setTab}
        items={[
          { id: 'pending', label: t('status.pending'), hint: desk.pending.length },
          { id: 'confirmed', label: t('status.confirmed'), hint: desk.confirmed.length },
        ]}
      />

      <div className="review-toolbar">
        <input
          className="search"
          placeholder={t('resale.search')}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>

      {error ? <p className="error">{error}</p> : null}

      <div className="resale-list">
        {filtered.length === 0 ? (
          <p className="muted">
            {tab === 'pending' ? t('resale.emptyPending') : t('resale.emptyConfirmed')}
          </p>
        ) : (
          filtered.map((item) => {
            if (item.kind === 'finalized') {
              const row = item.report;
              return (
                <article key={row.id} className="resale-list-card">
                  <Link to={deskItemHref(item)} className="resale-list-card-link">
                    <div className="resale-list-card-copy">
                      <div className="resale-badges">
                        <span className="pill status-sent">Confirmed</span>
                        {row.share_token && !row.share_revoked_at ? (
                          <span className="pill status-sent">Buyer link live</span>
                        ) : null}
                      </div>
                      <h3>{row.report_number}</h3>
                      <p className="review-meta muted">{confirmedCardMeta(row)}</p>
                      {row.seller_phone ? (
                        <p className="review-meta muted">Seller phone · {row.seller_phone}</p>
                      ) : null}
                    </div>
                    <div className="resale-score">
                      <strong>{row.overall_score}</strong>
                      <small>{row.market_condition?.replace(/_/g, ' ')}</small>
                    </div>
                  </Link>
                </article>
              );
            }

            const href = deskItemHref(item);
            const reportId = item.report?.id;
            const isWalkIn = item.kind === 'walkin';
            const confirming = reportId && confirmDeleteId === reportId;

            return (
              <article
                key={href}
                className={`resale-list-card resale-list-card-draft${
                  confirming ? ' resale-list-card-confirm' : ''
                }`}
              >
                <Link to={href} className="resale-list-card-link">
                  <div className="resale-list-card-copy">
                    <div className="resale-badges">
                      <span className="pill status-partial">{pendingBadge(item)}</span>
                      {item.report?.report_number ? (
                        <span className="pill">{item.report.report_number}</span>
                      ) : null}
                    </div>
                    <h3>{pendingCardTitle(item)}</h3>
                    <p className="review-meta muted">{pendingCardMeta(item)}</p>
                  </div>
                  <span className="material-symbols-outlined resale-chevron" aria-hidden>
                    chevron_right
                  </span>
                </Link>

                {isWalkIn && reportId ? (
                  <div className="resale-list-card-foot">
                    {confirming ? (
                      <>
                        <p className="resale-delete-prompt">Delete this walk-in draft?</p>
                        <div className="row-actions">
                          <button
                            type="button"
                            className="ghost danger"
                            disabled={deletingId === reportId}
                            onClick={(event) => deleteDraft(reportId, event)}
                          >
                            {deletingId === reportId ? 'Deleting…' : 'Yes, delete'}
                          </button>
                          <button
                            type="button"
                            className="ghost"
                            onClick={(event) => {
                              event.preventDefault();
                              event.stopPropagation();
                              setConfirmDeleteId(null);
                            }}
                          >
                            Cancel
                          </button>
                        </div>
                      </>
                    ) : (
                      <div className="row-actions">
                        <span className="muted resale-foot-hint">
                          Open to check in, inspect, and finalize
                        </span>
                        <button
                          type="button"
                          className="ghost danger"
                          onClick={(event) => deleteDraft(reportId, event)}
                        >
                          Delete draft
                        </button>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="resale-list-card-foot">
                    <span className="muted resale-foot-hint">
                      {item.kind === 'booking' && item.booking.status === 'pending'
                        ? 'Scheduled visit — open to check in when the car arrives'
                        : 'Open to continue the certified inspection workflow'}
                    </span>
                  </div>
                )}
              </article>
            );
          })
        )}
      </div>
    </section>
  );
}
